// src/pages/admin/ManageCreditPackages.tsx
import React, { useEffect, useState } from "react";
import {
  CreditPackage,
  createCreditPackage,
  updateCreditPackage,
  cancelCreditPackage,
  getCreditPackagesPage,
  CreditPackageStatus,
} from "../../firebase/creditPackages";
import type { QueryDocumentSnapshot, DocumentData } from "firebase/firestore";
import { getCoachUsers } from "../../firebase/users";
import {
  getRegistrationsByCreditPackage,
  Registration,
} from "../../firebase/registrations";
import { getAllCompetitions } from "../../firebase/competitions";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "../../hooks/useTranslation";
import Navbar from "../../components/Navbar";
import { C, FONT } from "../../theme";
import { formatDate as formatDateLocalized } from "../../utils/dateHelpers";

// ── Responsive hook ───────────────────────────────────────────────────────────
function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}

// ── Types ─────────────────────────────────────────────────────────────────────
interface CoachUser {
  uid: string;
  email: string;
  displayName: string;
}

interface PackageForm {
  coachId: string;
  competitionId: string;
  creditsTotal: string;
  amountPaid: string;
  bankReference: string;
  notes: string;
}

const EMPTY_FORM: PackageForm = {
  coachId: "",
  competitionId: "",
  creditsTotal: "",
  amountPaid: "",
  bankReference: "",
  notes: "",
};

// ── Helpers ───────────────────────────────────────────────────────────────────
async function fetchCoaches(): Promise<CoachUser[]> {
  // Cached, shared across admin pages — see firebase/users.ts
  const users = await getCoachUsers();
  return users.map((u) => ({
    uid: u.uid,
    email: u.email,
    displayName: u.displayName,
  }));
}

function formatDate(ts: any): string {
  return formatDateLocalized(ts);
}

function creditsRemaining(pkg: CreditPackage): number {
  return pkg.creditsTotal - pkg.creditsUsed;
}

// ── Component ─────────────────────────────────────────────────────────────────
export default function ManageCreditPackages() {
  const { currentUser } = useAuth();
  const { t } = useTranslation();
  const w = useWindowWidth();
  const isMobile = w < 768;

  // ── Data state
  const [packages, setPackages] = useState<CreditPackage[]>([]);
  const [coaches, setCoaches] = useState<CoachUser[]>([]);
  const [competitions, setCompetitions] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingMore, setLoadingMore] = useState<boolean>(false);

  // ── Filter state. filterStatus is now pushed down into the Firestore
  // query rather than evaluated client-side, so the on-the-wire payload only
  // contains rows in the selected status.
  const [filterStatus, setFilterStatus] = useState<"all" | CreditPackageStatus>("all");
  const [filterCoachId, setFilterCoachId] = useState<string>("all");
  // Global free-text search — matches coach name/email, competition name,
  // bank reference, and notes. Stays client-side because Firestore can't
  // do substring search.
  const [search, setSearch] = useState<string>("");

  // ── Server-side pagination state
  const [pageSize, setPageSize] = useState<number>(10);
  const [cursor, setCursor] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState<boolean>(false);

  // ── Modal state
  const [showModal, setShowModal] = useState<boolean>(false);
  const [editingPackage, setEditingPackage] = useState<CreditPackage | null>(
    null
  );
  const [form, setForm] = useState<PackageForm>(EMPTY_FORM);
  const [saving, setSaving] = useState<boolean>(false);
  const [formError, setFormError] = useState<string>("");

  // ── Cancel confirmation
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  // ── Usage expansion
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [usageMap, setUsageMap] = useState<Record<string, Registration[]>>({});
  const [usageLoading, setUsageLoading] = useState<boolean>(false);
  const [usageError, setUsageError] = useState<Record<string, string>>({});

  // ── Load coaches + competitions once on mount (small, cached lookups)
  useEffect(() => {
    Promise.all([fetchCoaches(), getAllCompetitions()])
      .then(([coachs, comps]) => {
        setCoaches(coachs);
        setCompetitions(comps as any[]);
      })
      .catch(console.error);
  }, []);

  // ── (Re)load the first page whenever the server-side filters change:
  // status filter or page size. Coach filter and free-text search are
  // applied client-side (no equivalent Firestore index for substring
  // search, and the dataset after server filter is small).
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getCreditPackagesPage({
      pageSize,
      status: filterStatus,
      startAfterDoc: null,
    })
      .then((page) => {
        if (cancelled) return;
        setPackages(page.rows);
        setCursor(page.cursor);
        setHasMore(page.hasMore);
      })
      .catch(console.error)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [filterStatus, pageSize]);

  // ── Filtered list (client-side: coach + search only)
  const filtered = packages.filter((p) => {
    if (filterCoachId !== "all" && p.coachId !== filterCoachId) return false;
    const q = search.trim().toLowerCase();
    if (q) {
      const hay = [
        p.coachDisplayName,
        p.coachEmail,
        p.competitionName,
        p.bankReference,
        p.notes,
      ]
        .map((x) => (x ?? "").toLowerCase())
        .join(" ");
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Server pagination drives the visible set — extend it with Load more.
  const paged = filtered;

  const loadMore = async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const page = await getCreditPackagesPage({
        pageSize,
        status: filterStatus,
        startAfterDoc: cursor,
      });
      setPackages((prev) => [...prev, ...page.rows]);
      setCursor(page.cursor);
      setHasMore(page.hasMore);
    } catch (err) {
      console.error("Load more credit packages failed:", err);
    } finally {
      setLoadingMore(false);
    }
  };

  // ── Modal helpers
  const openNewModal = () => {
    setEditingPackage(null);
    setForm(EMPTY_FORM);
    setFormError("");
    setShowModal(true);
  };

  const openEditModal = (pkg: CreditPackage) => {
    setEditingPackage(pkg);
    setForm({
      coachId: pkg.coachId,
      competitionId: pkg.competitionId,
      creditsTotal: String(pkg.creditsTotal),
      amountPaid: String(pkg.amountPaid),
      bankReference: pkg.bankReference ?? "",
      notes: pkg.notes ?? "",
    });
    setFormError("");
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPackage(null);
    setForm(EMPTY_FORM);
    setFormError("");
  };

  // ── Save handler (create or update)
  const handleSave = async () => {
    setFormError("");

    if (!form.coachId) {
      setFormError(t("credits.error.noCoach"));
      return;
    }
    if (!form.competitionId) {
      setFormError(t("credits.error.noCompetition"));
      return;
    }
    const creditsNum = parseInt(form.creditsTotal, 10);
    if (isNaN(creditsNum) || creditsNum < 1) {
      setFormError(t("credits.error.invalidCredits"));
      return;
    }
    const amountNum = parseFloat(form.amountPaid);
    if (isNaN(amountNum) || amountNum < 0) {
      setFormError(t("credits.error.invalidAmount"));
      return;
    }

    setSaving(true);
    try {
      if (editingPackage) {
        // Edit mode — only allow changing credits, amount, ref, notes
        await updateCreditPackage(editingPackage.id!, {
          creditsTotal: creditsNum,
          amountPaid: amountNum,
          bankReference: form.bankReference,
          notes: form.notes,
        });
        setPackages((prev) =>
          prev.map((p) =>
            p.id === editingPackage.id
              ? {
                  ...p,
                  creditsTotal: creditsNum,
                  amountPaid: amountNum,
                  bankReference: form.bankReference,
                  notes: form.notes,
                }
              : p
          )
        );
      } else {
        // New package
        const selectedCoach = coaches.find((c) => c.uid === form.coachId);
        const selectedComp =
          form.competitionId === "any"
            ? null
            : competitions.find((c) => c.id === form.competitionId);

        const newId = await createCreditPackage({
          coachId: form.coachId,
          coachEmail: selectedCoach?.email ?? "",
          coachDisplayName: selectedCoach?.displayName ?? "",
          competitionId: form.competitionId,
          competitionName:
            form.competitionId === "any"
              ? t("credits.anyCompetition")
              : selectedComp?.name ?? "",
          creditsTotal: creditsNum,
          amountPaid: amountNum,
          paymentMethod: "bank_transfer",
          bankReference: form.bankReference,
          createdBy: currentUser?.uid ?? "",
          notes: form.notes,
          status: "active",
        });

        // Reload first page so the new package appears on top.
        const fresh = await getCreditPackagesPage({
          pageSize,
          status: filterStatus,
          startAfterDoc: null,
        });
        setPackages(fresh.rows);
        setCursor(fresh.cursor);
        setHasMore(fresh.hasMore);
        void newId;
      }
      closeModal();
    } catch (err) {
      console.error("Save credit package failed:", err);
      setFormError(t("credits.error.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  // ── Cancel handler
  const handleCancel = async (packageId: string) => {
    try {
      await cancelCreditPackage(packageId);
      setPackages((prev) =>
        prev.map((p) =>
          p.id === packageId ? { ...p, status: "cancelled" } : p
        )
      );
    } catch (err) {
      console.error("Cancel failed:", err);
    } finally {
      setConfirmCancelId(null);
    }
  };

  // ── Usage expansion
  const handleToggleUsage = async (packageId: string) => {
    if (expandedId === packageId) {
      setExpandedId(null);
      return;
    }
    setExpandedId(packageId);
    // Always re-fetch if there was a previous error, otherwise skip if already loaded
    const hadError = !!usageError[packageId];
    if (!usageMap[packageId] || hadError) {
      setUsageLoading(true);
      setUsageError((prev) => { const n = { ...prev }; delete n[packageId]; return n; });
      try {
        const regs = await getRegistrationsByCreditPackage(packageId);
        setUsageMap((prev) => ({ ...prev, [packageId]: regs }));
      } catch (err: any) {
        console.error("Failed to load usage:", err);
        // Mark as empty so we don't retry on every expand, but show error message
        setUsageMap((prev) => ({ ...prev, [packageId]: [] }));
        setUsageError((prev) => ({
          ...prev,
          [packageId]:
            "Failed to load usage. If this is a new credit package, ensure the Firestore composite index exists: registrations → creditPackageId (Asc) + registeredAt (Desc).",
        }));
      } finally {
        setUsageLoading(false);
      }
    }
  };

  // ── Status badge
  const statusStyle = (
    status: CreditPackage["status"]
  ): React.CSSProperties => {
    if (status === "active")
      return {
        backgroundColor: "var(--color-success-soft)",
        color: "var(--color-success)",
        border: "1px solid #6ee7b7",
      };
    if (status === "exhausted")
      return {
        backgroundColor: "var(--color-warning-soft)",
        color: "var(--color-warning)",
        border: `1px solid ${C.warningBorder}`,
      };
    return {
      backgroundColor: C.surface2,
      color: C.text3,
      border: `1px solid ${C.border}`,
    };
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={s.page} className="page-content">
      <Navbar />

      {/* Cancel confirmation overlay */}
      {confirmCancelId && (
        <div style={s.overlay}>
          <div style={s.confirmModal}>
            <div style={s.confirmTitle}>{t("credits.cancel.confirm")}</div>
            <div style={s.confirmSub}>{t("credits.cancel.confirmSub")}</div>
            <div style={s.confirmActions}>
              <button
                style={s.cancelBtn}
                onClick={() => setConfirmCancelId(null)}
              >
                {t("credits.cancel.no")}
              </button>
              <button
                style={s.dangerBtn}
                onClick={() => handleCancel(confirmCancelId)}
              >
                {t("credits.cancel.yes")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Package modal */}
      {showModal && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, width: isMobile ? "calc(100vw - 32px)" : "520px" }}>
            <div style={s.modalAccent} />
            <div style={s.modalBody}>
              <div style={s.modalTitle}>
                {editingPackage
                  ? t("credits.modal.editTitle")
                  : t("credits.modal.newTitle")}
              </div>

              {/* Coach selector (read-only in edit mode) */}
              <div style={s.fieldGroup}>
                <div style={s.fieldLabel}>{t("credits.modal.coach")}</div>
                {editingPackage ? (
                  <div style={s.readonlyField}>
                    {editingPackage.coachDisplayName} ({editingPackage.coachEmail})
                  </div>
                ) : (
                  <select
                    style={s.select}
                    value={form.coachId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, coachId: e.target.value }))
                    }
                  >
                    <option value="">{t("credits.modal.selectCoach")}</option>
                    {coaches.map((c) => (
                      <option key={c.uid} value={c.uid}>
                        {c.displayName} — {c.email}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {/* Competition selector (read-only in edit mode) */}
              <div style={s.fieldGroup}>
                <div style={s.fieldLabel}>{t("credits.modal.competition")}</div>
                {editingPackage ? (
                  <div style={s.readonlyField}>
                    {editingPackage.competitionName}
                  </div>
                ) : (
                  <select
                    style={s.select}
                    value={form.competitionId}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, competitionId: e.target.value }))
                    }
                  >
                    <option value="">
                      {t("credits.modal.selectCompetition")}
                    </option>
                    <option value="any">
                      ★ {t("credits.anyCompetition")}
                    </option>
                    {competitions
                      .filter((c) => c.status === "open")
                      .map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                  </select>
                )}
              </div>

              <div style={isMobile ? s.fieldGroupFull : s.formRow}>
                {/* Credits total */}
                <div style={s.fieldGroup}>
                  <div style={s.fieldLabel}>{t("credits.modal.credits")}</div>
                  <input
                    style={s.input}
                    type="number"
                    min="1"
                    value={form.creditsTotal}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, creditsTotal: e.target.value }))
                    }
                    placeholder="e.g. 10"
                  />
                </div>

                {/* Amount paid */}
                <div style={s.fieldGroup}>
                  <div style={s.fieldLabel}>
                    {t("credits.modal.amountPaid")} (GEL)
                  </div>
                  <input
                    style={s.input}
                    type="number"
                    min="0"
                    step="0.01"
                    value={form.amountPaid}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, amountPaid: e.target.value }))
                    }
                    placeholder="e.g. 300"
                  />
                </div>
              </div>

              {/* Bank reference */}
              <div style={s.fieldGroup}>
                <div style={s.fieldLabel}>
                  {t("credits.modal.bankRef")} ({t("credits.modal.optional")})
                </div>
                <input
                  style={s.input}
                  type="text"
                  value={form.bankReference}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, bankReference: e.target.value }))
                  }
                  placeholder={t("credits.modal.bankRefPlaceholder")}
                />
              </div>

              {/* Notes */}
              <div style={s.fieldGroup}>
                <div style={s.fieldLabel}>
                  {t("credits.modal.notes")} ({t("credits.modal.optional")})
                </div>
                <textarea
                  style={{ ...s.input, height: "72px", resize: "vertical" }}
                  value={form.notes}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, notes: e.target.value }))
                  }
                  placeholder={t("credits.modal.notesPlaceholder")}
                />
              </div>

              {formError && <div style={s.errorBox}>{formError}</div>}

              <div style={s.modalActions}>
                <button
                  style={s.secondaryBtn}
                  onClick={closeModal}
                  disabled={saving}
                >
                  {t("credits.modal.cancel")}
                </button>
                <button
                  style={saving ? s.primaryBtnDisabled : s.primaryBtn}
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving
                    ? t("credits.modal.saving")
                    : editingPackage
                    ? t("credits.modal.saveEdit")
                    : t("credits.modal.save")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div style={{ ...s.container, padding: isMobile ? "24px 12px 48px" : "40px 24px" }}>
        {/* ── Page header */}
        <div
          style={{
            ...s.pageHeader,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "12px" : undefined,
          }}
        >
          <div>
            <div style={s.pageLabel}>{t("credits.pageLabel")}</div>
            <div
              style={{
                ...s.heading,
                fontSize: isMobile ? "24px" : "32px",
              }}
            >
              {t("credits.heading")}
            </div>
            <div style={s.subheading}>{t("credits.subheading")}</div>
          </div>
          <button
            style={{ ...s.newBtn, alignSelf: isMobile ? "flex-start" : undefined }}
            onClick={openNewModal}
          >
            + {t("credits.newButton")}
          </button>
        </div>

        {/* ── Filters */}
        <div
          style={{
            ...s.filterRow,
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <div
            style={{
              ...s.filterGroup,
              flex: isMobile ? "1 1 100%" : "1 1 240px",
            }}
          >
            <div style={s.filterLabel}>Search</div>
            <input
              type="text"
              style={s.filterSelect}
              placeholder="Coach, competition, bank ref, notes…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div style={s.filterGroup}>
            <div style={s.filterLabel}>{t("credits.filter.status")}</div>
            <select
              style={s.filterSelect}
              value={filterStatus}
              onChange={(e) =>
                setFilterStatus(
                  e.target.value as "all" | "active" | "exhausted" | "cancelled"
                )
              }
            >
              <option value="all">{t("credits.filter.all")}</option>
              <option value="active">{t("credits.status.active")}</option>
              <option value="exhausted">{t("credits.status.exhausted")}</option>
              <option value="cancelled">{t("credits.status.cancelled")}</option>
            </select>
          </div>
          <div style={s.filterGroup}>
            <div style={s.filterLabel}>{t("credits.filter.coach")}</div>
            <select
              style={s.filterSelect}
              value={filterCoachId}
              onChange={(e) => setFilterCoachId(e.target.value)}
            >
              <option value="all">{t("credits.filter.allCoaches")}</option>
              {coaches.map((c) => (
                <option key={c.uid} value={c.uid}>
                  {c.displayName}
                </option>
              ))}
            </select>
          </div>
          <div style={s.filterCount}>
            {filtered.length} {t("credits.filter.results")}
          </div>
        </div>

        {/* ── Loading */}
        {loading && (
          <div style={s.stateBox}>
            <div style={s.spinner} />
            <div style={{ ...s.stateText, marginTop: 16 }}>
              {t("credits.loading")}
            </div>
          </div>
        )}

        {/* ── Empty */}
        {!loading && filtered.length === 0 && (
          <div style={s.stateBox}>
            <div style={s.emptyIcon}>💳</div>
            <div style={s.stateText}>{t("credits.empty.title")}</div>
            <div style={s.stateSub}>{t("credits.empty.sub")}</div>
          </div>
        )}

        {/* ── Desktop table */}
        {!loading && filtered.length > 0 && !isMobile && (
          <div style={s.tableCard}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>{t("credits.table.coach")}</th>
                  <th style={s.th}>{t("credits.table.competition")}</th>
                  <th style={s.th}>{t("credits.table.credits")}</th>
                  <th style={s.th}>{t("credits.table.amount")}</th>
                  <th style={s.th}>{t("credits.table.status")}</th>
                  <th style={s.th}>{t("credits.table.created")}</th>
                  <th style={s.th}>{t("credits.table.actions")}</th>
                </tr>
              </thead>
              <tbody>
                {paged.map((pkg) => (
                  <React.Fragment key={pkg.id}>
                    <tr style={s.tableRow}>
                      <td style={s.td}>
                        <div style={{ fontWeight: 600, color: C.text }}>
                          {pkg.coachDisplayName}
                        </div>
                        <div style={{ fontSize: "11px", color: C.text3 }}>
                          {pkg.coachEmail}
                        </div>
                      </td>
                      <td style={{ ...s.td, maxWidth: "200px" }}>
                        {pkg.competitionName}
                      </td>
                      <td style={s.td}>
                        <div style={s.creditBar}>
                          <span style={s.creditUsed}>{pkg.creditsUsed}</span>
                          <span style={s.creditSep}>/</span>
                          <span style={s.creditTotal}>{pkg.creditsTotal}</span>
                          <span style={s.creditRemLabel}>
                            ({creditsRemaining(pkg)} {t("credits.table.remaining")})
                          </span>
                        </div>
                      </td>
                      <td style={{ ...s.td, fontWeight: 700, color: C.text }}>
                        {pkg.amountPaid} GEL
                      </td>
                      <td style={s.td}>
                        <span
                          style={{
                            ...s.statusBadge,
                            ...statusStyle(pkg.status),
                          }}
                        >
                          {t(`credits.status.${pkg.status}`)}
                        </span>
                      </td>
                      <td style={s.td}>{formatDate(pkg.createdAt)}</td>
                      <td style={s.td}>
                        <div style={s.actionRow}>
                          <button
                            style={s.actionBtn}
                            onClick={() => handleToggleUsage(pkg.id!)}
                          >
                            {expandedId === pkg.id
                              ? t("credits.action.hideUsage")
                              : t("credits.action.viewUsage")}
                          </button>
                          {pkg.status !== "cancelled" && (
                            <>
                              <button
                                style={s.actionBtn}
                                onClick={() => openEditModal(pkg)}
                              >
                                {t("credits.action.edit")}
                              </button>
                              <button
                                style={s.dangerActionBtn}
                                onClick={() => setConfirmCancelId(pkg.id!)}
                              >
                                {t("credits.action.cancel")}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Usage expansion row */}
                    {expandedId === pkg.id && (
                      <tr>
                        <td colSpan={7} style={s.usageCell}>
                          {usageLoading ? (
                            <div style={{ padding: "12px", color: C.text2, fontSize: "13px" }}>
                              {t("credits.usage.loading")}
                            </div>
                          ) : usageError[pkg.id!] ? (
                            <div style={{ padding: "12px", color: "var(--color-danger)", fontSize: "12px", lineHeight: 1.5 }}>
                              ⚠️ {usageError[pkg.id!]}
                            </div>
                          ) : (usageMap[pkg.id!] ?? []).length === 0 ? (
                            <div style={{ padding: "12px", color: C.text3, fontSize: "13px" }}>
                              {t("credits.usage.empty")}
                            </div>
                          ) : (
                            <div style={s.usageInner}>
                              <div style={s.usageTitle}>
                                {t("credits.usage.title")} ({(usageMap[pkg.id!] ?? []).length})
                              </div>
                              {(usageMap[pkg.id!] ?? []).map((reg) => (
                                <div key={reg.id} style={s.usageRow}>
                                  <span style={{ fontWeight: 600, color: C.text }}>
                                    {reg.athleteSnapshot.firstName}{" "}
                                    {reg.athleteSnapshot.lastName}
                                  </span>
                                  <span style={{ color: C.text2, fontSize: "12px" }}>
                                    {reg.club} · {reg.weightCategory} ·{" "}
                                    {reg.division}
                                  </span>
                                  <span
                                    style={{
                                      fontFamily: "monospace",
                                      fontSize: "11px",
                                      color: C.text3,
                                    }}
                                  >
                                    {reg.referenceCode}
                                  </span>
                                  <span style={{ color: C.text3, fontSize: "12px" }}>
                                    {formatDate(reg.registeredAt)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>

            {/* ── Pagination footer: page-size selector + Load more.
                The page-size selector doubles as the Firestore `limit`,
                so changing it triggers a fresh first-page fetch. */}
            <div style={s.paginationRow}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{ fontSize: "12px", color: C.text3 }}>
                  {t("credits.table.rowsPerPage") || "Rows per page:"}
                </span>
                <select
                  style={s.selectSmall}
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              <span style={{ fontSize: "12px", color: C.text3 }}>
                {filtered.length} {t("credits.filter.results")}
                {hasMore && " · more available"}
              </span>

              {hasMore && (
                <button
                  style={loadingMore ? s.paginationBtn : s.paginationBtnActive}
                  onClick={loadMore}
                  disabled={loadingMore}
                >
                  {loadingMore ? "Loading…" : "Load more"}
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Mobile cards */}
        {!loading && filtered.length > 0 && isMobile && (
          <div style={s.cardList}>
            {filtered.map((pkg) => (
              <div key={pkg.id} style={s.mobileCard}>
                {/* Coach + status */}
                <div style={s.cardTopRow}>
                  <div>
                    <div style={s.cardCoach}>{pkg.coachDisplayName}</div>
                    <div style={s.cardEmail}>{pkg.coachEmail}</div>
                  </div>
                  <span
                    style={{ ...s.statusBadge, ...statusStyle(pkg.status) }}
                  >
                    {t(`credits.status.${pkg.status}`)}
                  </span>
                </div>

                {/* Competition */}
                <div style={s.cardComp}>{pkg.competitionName}</div>

                {/* Credits + amount */}
                <div style={s.cardMidRow}>
                  <div style={s.cardStat}>
                    <div style={s.cardStatNum}>{creditsRemaining(pkg)}</div>
                    <div style={s.cardStatLabel}>
                      {t("credits.table.remaining")}
                    </div>
                  </div>
                  <div style={s.cardStat}>
                    <div style={s.cardStatNum}>{pkg.creditsTotal}</div>
                    <div style={s.cardStatLabel}>
                      {t("credits.table.total")}
                    </div>
                  </div>
                  <div style={s.cardStat}>
                    <div style={s.cardStatNum}>{pkg.amountPaid}</div>
                    <div style={s.cardStatLabel}>GEL</div>
                  </div>
                </div>

                {/* Date + bank ref */}
                <div style={s.cardMeta}>
                  {formatDate(pkg.createdAt)}
                  {pkg.bankReference
                    ? ` · ${pkg.bankReference}`
                    : ""}
                </div>

                {/* Actions */}
                <div style={s.cardActions}>
                  <button
                    style={s.actionBtn}
                    onClick={() => handleToggleUsage(pkg.id!)}
                  >
                    {expandedId === pkg.id
                      ? t("credits.action.hideUsage")
                      : t("credits.action.viewUsage")}
                  </button>
                  {pkg.status !== "cancelled" && (
                    <>
                      <button
                        style={s.actionBtn}
                        onClick={() => openEditModal(pkg)}
                      >
                        {t("credits.action.edit")}
                      </button>
                      <button
                        style={s.dangerActionBtn}
                        onClick={() => setConfirmCancelId(pkg.id!)}
                      >
                        {t("credits.action.cancel")}
                      </button>
                    </>
                  )}
                </div>

                {/* Mobile usage expansion */}
                {expandedId === pkg.id && (
                  <div style={s.usageInner}>
                    {usageLoading ? (
                      <div style={{ color: C.text2, fontSize: "13px" }}>
                        {t("credits.usage.loading")}
                      </div>
                    ) : usageError[pkg.id!] ? (
                      <div style={{ color: "var(--color-danger)", fontSize: "12px", lineHeight: 1.5 }}>
                        ⚠️ {usageError[pkg.id!]}
                      </div>
                    ) : (usageMap[pkg.id!] ?? []).length === 0 ? (
                      <div style={{ color: C.text3, fontSize: "13px" }}>
                        {t("credits.usage.empty")}
                      </div>
                    ) : (
                      <>
                        <div style={s.usageTitle}>
                          {t("credits.usage.title")} (
                          {(usageMap[pkg.id!] ?? []).length})
                        </div>
                        {(usageMap[pkg.id!] ?? []).map((reg) => (
                          <div key={reg.id} style={s.usageRow}>
                            <span style={{ fontWeight: 600, color: C.text, fontSize: "13px" }}>
                              {reg.athleteSnapshot.firstName}{" "}
                              {reg.athleteSnapshot.lastName}
                            </span>
                            <span style={{ color: C.text2, fontSize: "12px" }}>
                              {reg.club} · {reg.weightCategory}
                            </span>
                          </div>
                        ))}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────
const s: Record<string, React.CSSProperties> = {
  page: { minHeight: "100vh", backgroundColor: "transparent" },
  container: { maxWidth: "1200px", margin: "0 auto" },

  pageHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "28px",
  },
  pageLabel: {
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "2px",
    color: C.red,
    marginBottom: "6px",
    textTransform: "uppercase",
  },
  heading: {
    fontFamily: FONT.display,
    letterSpacing: "2px",
    color: C.text,
    lineHeight: 1,
    marginBottom: "8px",
  },
  subheading: { fontSize: "14px", color: C.text2 },

  newBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "11px 22px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
    letterSpacing: "0.3px",
    whiteSpace: "nowrap",
  },

  filterRow: {
    display: "flex",
    gap: "12px",
    alignItems: "flex-end",
    marginBottom: "20px",
    flexWrap: "wrap",
  },
  filterGroup: { display: "flex", flexDirection: "column", gap: "4px" },
  filterLabel: {
    fontSize: "10px",
    fontWeight: 700,
    color: C.text3,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
  },
  filterSelect: {
    padding: "8px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "13px",
    color: C.text,
    backgroundColor: C.surface,
    outline: "none",
    cursor: "pointer",
    minWidth: "160px",
  },
  filterCount: {
    fontSize: "12px",
    color: C.text3,
    alignSelf: "flex-end",
    paddingBottom: "8px",
    marginLeft: "4px",
  },

  stateBox: { textAlign: "center", padding: "80px 24px" },
  emptyIcon: { fontSize: "40px", marginBottom: "16px" },
  stateText: { fontSize: "16px", color: C.text2, marginBottom: "8px" },
  stateSub: { fontSize: "13px", color: C.text3 },
  spinner: {
    width: "34px",
    height: "34px",
    margin: "0 auto",
    border: `3px solid ${C.border}`,
    borderTopColor: C.red,
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },

  tableCard: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "12px",
    overflow: "hidden",
  },
  table: { width: "100%", borderCollapse: "collapse" },
  th: {
    textAlign: "left",
    padding: "11px 16px",
    backgroundColor: C.surface2,
    color: C.text2,
    fontWeight: 700,
    fontSize: "11px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    borderBottom: `1px solid ${C.border}`,
    whiteSpace: "nowrap",
  },
  tableRow: { borderBottom: `1px solid ${C.border}` },
  td: {
    padding: "14px 16px",
    color: C.text2,
    fontSize: "13px",
    verticalAlign: "middle",
  },

  creditBar: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    flexWrap: "wrap",
  },
  creditUsed: { fontSize: "13px", fontWeight: 700, color: C.text },
  creditSep: { color: C.text3, fontSize: "13px" },
  creditTotal: { fontSize: "13px", color: C.text2 },
  creditRemLabel: { fontSize: "11px", color: C.text3 },

  statusBadge: {
    display: "inline-block",
    padding: "3px 9px",
    borderRadius: "20px",
    fontSize: "10px",
    fontWeight: 700,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },

  actionRow: { display: "flex", gap: "6px", flexWrap: "wrap" },
  actionBtn: {
    backgroundColor: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: "6px",
    color: C.text2,
    fontSize: "11px",
    fontWeight: 600,
    padding: "5px 10px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  dangerActionBtn: {
    backgroundColor: "transparent",
    border: `1px solid rgba(218,41,28,0.3)`,
    borderRadius: "6px",
    color: C.red,
    fontSize: "11px",
    fontWeight: 600,
    padding: "5px 10px",
    cursor: "pointer",
    whiteSpace: "nowrap",
  },

  usageCell: {
    backgroundColor: C.surface2,
    borderBottom: `1px solid ${C.border}`,
    padding: "0",
  },
  usageInner: {
    padding: "14px 24px",
    borderTop: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  usageTitle: {
    fontSize: "10px",
    fontWeight: 700,
    color: C.text3,
    textTransform: "uppercase",
    letterSpacing: "1px",
    marginBottom: "4px",
  },
  usageRow: {
    display: "flex",
    gap: "14px",
    alignItems: "center",
    flexWrap: "wrap",
    paddingBottom: "6px",
    borderBottom: `1px solid ${C.border}`,
  },

  // Mobile cards
  cardList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  mobileCard: {
    backgroundColor: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: "12px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
  },
  cardTopRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  cardCoach: { fontSize: "14px", fontWeight: 700, color: C.text },
  cardEmail: { fontSize: "11px", color: C.text3, marginTop: "2px" },
  cardComp: { fontSize: "13px", color: C.text2 },
  cardMidRow: { display: "flex", gap: "16px" },
  cardStat: { textAlign: "center" },
  cardStatNum: {
    fontFamily: FONT.display,
    fontSize: "22px",
    color: C.text,
    lineHeight: 1,
  },
  cardStatLabel: {
    fontSize: "9px",
    color: C.text3,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    marginTop: "2px",
  },
  cardMeta: { fontSize: "11px", color: C.text3 },
  cardActions: { display: "flex", gap: "6px", flexWrap: "wrap" },

  // Modal
  overlay: {
    position: "fixed",
    inset: 0,
    backgroundColor: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 200,
    padding: "16px",
  },
  modal: {
    backgroundColor: C.surface,
    borderRadius: "14px",
    overflow: "hidden",
    boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
    maxHeight: "90vh",
    overflowY: "auto",
  },
  modalAccent: { height: "4px", backgroundColor: C.red },
  modalBody: {
    padding: "24px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  modalTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: C.text,
    marginBottom: "4px",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  fieldGroupFull: {
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  formRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "14px",
  },
  fieldLabel: {
    fontSize: "10px",
    fontWeight: 700,
    color: C.text2,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    padding: "10px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "13px",
    color: C.text,
    backgroundColor: C.surface2,
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: FONT.body,
  },
  select: {
    padding: "10px 12px",
    border: `1px solid ${C.border2}`,
    borderRadius: "7px",
    fontSize: "13px",
    color: C.text,
    backgroundColor: C.surface2,
    outline: "none",
    cursor: "pointer",
    width: "100%",
  },
  readonlyField: {
    padding: "10px 12px",
    border: `1px solid ${C.border}`,
    borderRadius: "7px",
    fontSize: "13px",
    color: C.text2,
    backgroundColor: C.surface2,
  },
  errorBox: {
    backgroundColor: "var(--color-danger-soft)",
    border: `1px solid ${C.red}`,
    borderRadius: "7px",
    padding: "10px 14px",
    fontSize: "13px",
    color: C.red,
  },
  modalActions: {
    display: "flex",
    gap: "10px",
    justifyContent: "flex-end",
    marginTop: "4px",
  },
  primaryBtn: {
    backgroundColor: C.red,
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 22px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "pointer",
  },
  primaryBtnDisabled: {
    backgroundColor: C.border,
    color: C.text3,
    border: "none",
    borderRadius: "8px",
    padding: "10px 22px",
    fontSize: "13px",
    fontWeight: 700,
    cursor: "not-allowed",
  },
  secondaryBtn: {
    backgroundColor: "transparent",
    border: `1px solid ${C.border2}`,
    borderRadius: "8px",
    color: C.text2,
    fontSize: "13px",
    fontWeight: 600,
    padding: "10px 20px",
    cursor: "pointer",
  },

  // Pagination
  paginationRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: "12px",
    padding: "12px 16px",
    borderTop: `1px solid ${C.border}`,
    backgroundColor: C.surface2,
  },
  selectSmall: {
    padding: "5px 10px",
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    fontSize: "12px",
    color: C.text,
    backgroundColor: C.surface,
    outline: "none",
    cursor: "pointer",
  },
  paginationBtn: {
    minWidth: "32px",
    height: "32px",
    padding: "0 8px",
    border: `1px solid ${C.border2}`,
    borderRadius: "6px",
    backgroundColor: C.surface,
    color: C.text2,
    fontSize: "12px",
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT.body,
    transition: "background 0.12s, color 0.12s",
  },
  paginationBtnActive: {
    minWidth: "32px",
    height: "32px",
    padding: "0 8px",
    border: `1px solid ${C.red}`,
    borderRadius: "6px",
    backgroundColor: C.red,
    color: "#fff",
    fontSize: "12px",
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT.body,
  },

  // Cancel confirm
  confirmModal: {
    backgroundColor: C.surface,
    borderRadius: "12px",
    padding: "28px",
    maxWidth: "380px",
    width: "100%",
    boxShadow: "0 16px 48px rgba(0,0,0,0.2)",
  },
  confirmTitle: {
    fontSize: "16px",
    fontWeight: 700,
    color: C.text,
    marginBottom: "8px",
  },
  confirmSub: {
    fontSize: "13px",
    color: C.text2,
    marginBottom: "20px",
    lineHeight: 1.6,
  },
  confirmActions: { display: "flex", gap: "10px", justifyContent: "flex-end" },
  cancelBtn: {
    backgroundColor: "transparent",
    border: `1px solid ${C.border2}`,
    borderRadius: "8px",
    color: C.text2,
    fontSize: "13px",
    fontWeight: 600,
    padding: "9px 18px",
    cursor: "pointer",
  },
  dangerBtn: {
    backgroundColor: C.redDim,
    border: `1px solid ${C.red}`,
    borderRadius: "8px",
    color: C.red,
    fontSize: "13px",
    fontWeight: 700,
    padding: "9px 18px",
    cursor: "pointer",
  },
};
